"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  FiSearch,
  FiFilter,
  FiX,
  FiMapPin,
  FiCalendar,
  FiRefreshCw,
  FiUser,
  FiCheckSquare,
  FiAlertCircle,
  FiLock,
  FiSettings,
} from "react-icons/fi"
import { LuEye } from "react-icons/lu"
import { Link } from "react-router-dom"
import { Pagination } from "../../../utils/pagination.utils"
import { ExcelDownload, formatExcelDate } from "../../../utils/excel.utils"
import { useAuth } from "../../../provider/auth.provider"
import { formatDate } from "../../activities/edit"
import axiosInstance from "../../../libs/axios"
import { FaClock, FaCheckCircle, FaTimesCircle, FaExclamationCircle, FaSearch, FaFileExcel, FaCalendarAlt, FaSort, FaSortUp, FaSortDown, FaRupeeSign, FaUniversity, FaMoneyBillWave, FaClipboardCheck } from "react-icons/fa"
import { SelectWithSearch2, type SelectOption } from "../../../utils/select-with-search"
import { useDebouncedCallback } from "../../../hooks/useDebounce"
import { FaIndianRupeeSign } from "react-icons/fa6"
import TimeInput24h from "../../../utils/time-input-24h"

import { useUrlFilters } from "../../../hooks/useUrlFilters";

/** Query params a dashboard tile may pre-apply to this list. */
export const URL_FILTER_KEYS = ["status", "user_id", "project_id", "date_from", "date_to", "search"];

const formatDateOnly = (dateString: string) => {
  return new Date(dateString + 'T00:00:00').toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: '2-digit'
  });
};

interface Advance {
  id: number
  employee_id: number
  accounting_name: string | null
  request_amount: number
  advance_reason: string
  request_remarks: string
  status: string
  approved_by: number | null
  approved_date: string | null
  approved_amount: number | null
  approval_remarks: string | null
  rejection_reason: string | null
  rejected_at: string | null
  rejected_by: number | null
  transferred_by: number | null
  transferred_amount?: number | null
  transfer_mode?: string | null
  transfer_reference?: string | null
  transfer_remarks?: string | null
  transferred_date: string | null
  transferred_time?: string | null
  completed_by?: number | null
  completed_at?: string | null
  completion_remarks?: string | null
  completer?: { id: number; first_name: string; last_name: string } | null
  created_at: string
  updated_at: string
  payment_qr_code?: string | null
  use_existing_qr?: boolean | null
  bank_name?: string | null
  bank_account_number?: string | null
  ifsc_code?: string | null
  // Frozen on the day of the request — preferred over the live employee row.
  snapshot_circle_name?: string | null
  snapshot_reporting_person_name?: string | null
  snapshot_department_group_name?: string | null
  snapshot_taken_at?: string | null
  employee: {
    id: number
    first_name: string
    last_name: string
    association_type: string
    profile_picture: string | null
    employee_code?: string
    state?: {
      id: number
      circle_name?: string
    }
    department?: {
      id: number
      name?: string
    } | null
    designation?: {
      id: number
      name?: string
    } | null
    user_projects?: Array<{
      project_id: number
      project?: {
        id: number
        project_name?: string
        client_project?: {
          id?: number
          client_project_name?: string
        } | null
      } | null
    }> | null
    project_manager?: {
      id: number
      first_name?: string
      last_name?: string
    } | null
    user_details?: {
      bank_name?: string | null
      bank_account_number?: string | null
      ifsc_code?: string | null
      payment_qr_code?: string | null
    } | null
  }
  approver: {
    id: number
    first_name: string
    last_name: string
    association_type: string
    profile_picture: string
  } | null
  rejector: any | null
  transferrer: {
    id: number
    first_name: string
    last_name: string
    association_type: string
    profile_picture: string
  } | null
}

interface PaginationType {
  total_count: number
  page: number
  limit: number
  totalPages: number
}

export interface AdvanceFilters {
  search?: string
  employee_id?: string
  employee_name?: string
  status?: string
  reason?: string
  state_id?: string
  district_id?: string
  amount_min?: string
  amount_max?: string
  request_date_from?: string
  request_date_to?: string
  created_from?: string
  created_to?: string
  created_at_from?: string
  created_at_to?: string
  approved_by?: string
  approved_by_name?: string
  is_approved?: string
  is_repaid?: string
  repayment_pending?: string
  transfer_date_from?: string
  transfer_date_to?: string
  approve_date_from?: string
  approve_date_to?: string
  sortBy?: string
  sortOrder?: string
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: AdvanceFilters
  onFiltersChange: (filters: AdvanceFilters) => void
  onApply: () => void
  onReset: () => void
}

const AdvanceFilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset
}) => {
  const [activeTab, setActiveTab] = useState('basic')
  const [selectedApprover, setSelectedApprover] = useState<SelectOption | null>(
    filters.approved_by
      ? {
        id: Number(filters.approved_by),
        value: Number(filters.approved_by),
        label: filters.approved_by_name || `User ID: ${filters.approved_by}`,
      }
      : null
  )
  const [selectedEmployee, setSelectedEmployee] = useState<SelectOption | null>(
    filters.employee_id
      ? {
        id: Number(filters.employee_id),
        value: Number(filters.employee_id),
        label: filters.employee_name || `Employee ID: ${filters.employee_id}`,
      }
      : null
  )

  useEffect(() => {
    if (filters.approved_by) {
      setSelectedApprover({
        id: Number(filters.approved_by),
        value: Number(filters.approved_by),
        label: filters.approved_by_name || `User ID: ${filters.approved_by}`,
      })
    } else {
      setSelectedApprover(null)
    }
  }, [filters.approved_by, filters.approved_by_name])

  useEffect(() => {
    if (filters.employee_id) {
      setSelectedEmployee({
        id: Number(filters.employee_id),
        value: Number(filters.employee_id),
        label: filters.employee_name || `Employee ID: ${filters.employee_id}`,
      })
    } else {
      setSelectedEmployee(null)
    }
  }, [filters.employee_id, filters.employee_name])

  if (!isOpen) return null

  const handleInputChange = (field: keyof AdvanceFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined
    })
  }

  const handleApproverSelect = (option: SelectOption | null) => {
    setSelectedApprover(option)
    onFiltersChange({
      ...filters,
      approved_by: option ? String(option.value) : undefined,
      approved_by_name: option ? option.label : undefined,
    })
  }

  const handleEmployeeSelect = (option: SelectOption | null) => {
    setSelectedEmployee(option)
    onFiltersChange({
      ...filters,
      employee_id: option ? String(option.value) : undefined,
      employee_name: option ? option.label : undefined,
    })
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiSearch },
    // { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'amount', label: 'Amounts', icon: FaRupeeSign },
    { id: 'dates', label: 'Dates', icon: FiCalendar },
    { id: 'approval', label: 'Approval', icon: FiCheckSquare },
    { id: 'other', label: 'Other', icon: FiUser }
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Search by requestor name, remarks, employee code..."
                  value={filters.search || ''}
                  onChange={(e) => handleInputChange('search', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="requested">Requested</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="transferred">Transferred</option>
                  <option value="completed">Completed</option>
                  <option value="paid">Paid</option>
                  <option value="repaid">Repaid</option>
                </select>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Reason"
                  value={filters.reason || ''}
                  onChange={(e) => handleInputChange('reason', e.target.value)}
                />
              </div> */}
            </div>
          )}

          {/* Location Tab */}
          {/* {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="State ID"
                  value={filters.state_id || ''}
                  onChange={(e) => handleInputChange('state_id', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">District ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="District ID"
                  value={filters.district_id || ''}
                  onChange={(e) => handleInputChange('district_id', e.target.value)}
                />
              </div>
            </div>
          )} */}

          {/* Amounts Tab */}
          {activeTab === 'amount' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Min Amount"
                  value={filters.amount_min || ''}
                  onChange={(e) => handleInputChange('amount_min', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Max Amount"
                  value={filters.amount_max || ''}
                  onChange={(e) => handleInputChange('amount_max', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Dates Tab */}
          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Request Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.request_date_from || ''}
                  onChange={(e) => handleInputChange('request_date_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Request Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.request_date_to || ''}
                  onChange={(e) => handleInputChange('request_date_to', e.target.value)}
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Request Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ''}
                  onChange={(e) => handleInputChange('created_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Request Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ''}
                  onChange={(e) => handleInputChange('created_to', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.transfer_date_from || ''}
                  onChange={(e) => handleInputChange('transfer_date_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.transfer_date_to || ''}
                  onChange={(e) => handleInputChange('transfer_date_to', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approve Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.approve_date_from || ''}
                  onChange={(e) => handleInputChange('approve_date_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approve Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.approve_date_to || ''}
                  onChange={(e) => handleInputChange('approve_date_to', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Approval Tab */}
          {activeTab === 'approval' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approved By</label>
                <SelectWithSearch2
                  placeholder="Search approver"
                  value={selectedApprover}
                  onChange={handleApproverSelect}
                  apiEndpoint="/users/list"
                  mapOption={(user: any): SelectOption => ({
                    id: user.id,
                    value: user.id,
                    label:
                      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
                      user.email ||
                      `ID ${user.id}`,
                  })}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Is Approved</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.is_approved || ''}
                  onChange={(e) => handleInputChange('is_approved', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Is Repaid</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.is_repaid || ''}
                  onChange={(e) => handleInputChange('is_repaid', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Repayment Pending</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.repayment_pending || ''}
                  onChange={(e) => handleInputChange('repayment_pending', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div> */}
            </div>
          )}

          {/* Other Tab */}
          {activeTab === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                <SelectWithSearch2
                  placeholder="Search employee"
                  value={selectedEmployee}
                  onChange={handleEmployeeSelect}
                  apiEndpoint="/users/list"
                  mapOption={(user: any): SelectOption => ({
                    id: user.id,
                    value: user.id,
                    label:
                      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
                      user.email ||
                      `ID ${user.id}`,
                  })}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply()
                onClose()
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface AdvancesListProps {
  advances: Advance[]
  pagination: PaginationType
  onSearch: (search: string) => void
  onFilterChange: (filter: string) => void
  statusFilter: string
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdvancedFilter?: (filters: AdvanceFilters) => void
  loading?: boolean
  sortConfig?: {
    field: string
    direction: "ASC" | "DESC"
  }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  appliedFilters?: AdvanceFilters
  searchValue?: string
  onRefresh?: () => void
  onAdvanceCreated?: () => void
  canViewAllAdvances?: boolean
  // Restriction props
  isBlocked?: boolean
  blockReason?: string | null
  userLimit?: { max_amount: number; reason: string | null } | null
}

export const AdvancesList: React.FC<AdvancesListProps> = ({
  advances,
  pagination,
  onSearch,
  onFilterChange,
  statusFilter,
  onPageChange,
  onLimitChange,
  onAdvancedFilter,
  loading = false,
  sortConfig,
  onSort,
  appliedFilters,
  searchValue = "",
  onRefresh,
  onAdvanceCreated,
  canViewAllAdvances = false,
  isBlocked = false,
  blockReason = null,
  userLimit = null,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const { hasPermission, user } = useAuth()
  const [filters, setFilters] = useState<AdvanceFilters>(useUrlFilters<AdvanceFilters>({}, URL_FILTER_KEYS))
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
  const [requestSubmitting, setRequestSubmitting] = useState(false)
  const [requestForm, setRequestForm] = useState({
    request_amount: "",
    advance_reason: "site_work",
    request_remarks: "",
  })
  const [qrOption, setQrOption] = useState<'none' | 'existing' | 'new'>('none')
  const [hasProfileQr, setHasProfileQr] = useState<boolean | null>(null)
  const [newQrFile, setNewQrFile] = useState<File | null>(null)
  const [newQrUploading, setNewQrUploading] = useState(false)
  const [newQrUrl, setNewQrUrl] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [paymentInfoAdvance, setPaymentInfoAdvance] = useState<Advance | null>(null)
  const [transferAdvance, setTransferAdvance] = useState<Advance | null>(null)
  const [transferData, setTransferData] = useState({ transferred_amount: 0, transfer_mode: 'bank_transfer', transfer_reference: '', transfer_remarks: '', transfer_date: '', transfer_time: '' })
  const [transferErrors, setTransferErrors] = useState<Record<string, string>>({})
  const [transferTouched, setTransferTouched] = useState<Record<string, boolean>>({})
  const [transferLoading, setTransferLoading] = useState(false)
  const [completeAdvance, setCompleteAdvance] = useState<Advance | null>(null)
  const [completeData, setCompleteData] = useState({ transfer_reference: '', completion_remarks: '' })
  const [completeError, setCompleteError] = useState('')
  const [completeLoading, setCompleteLoading] = useState(false)
  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  useEffect(() => {
    setSearchTerm(searchValue)
  }, [searchValue])

  // When the request modal opens, check whether the requester has a payment QR
  // saved in their profile. The "Use existing QR" option is only valid if so —
  // otherwise the advance would be saved with no QR and nothing renders later.
  useEffect(() => {
    if (!isRequestModalOpen || !user?.id) return
    let cancelled = false
    setHasProfileQr(null)
    axiosInstance
      .get(`/user-details/${user.id}/details`)
      .then((res) => {
        if (cancelled) return
        const qr = res?.data?.data?.user_details?.payment_qr_code
        setHasProfileQr(Boolean(qr))
      })
      .catch(() => {
        if (!cancelled) setHasProfileQr(false)
      })
    return () => {
      cancelled = true
    }
  }, [isRequestModalOpen, user?.id])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    onFilterChange(value)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
  }

  const handleRequestAdvance = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isBlocked) return
    const amount = Number(requestForm.request_amount)
    if (userLimit && amount > userLimit.max_amount) {
      import("react-hot-toast").then(({ default: toast }) =>
        toast.error(`Amount exceeds your limit of ₹${userLimit!.max_amount.toLocaleString()}`)
      )
      return
    }

    let uploadedQrUrl: string | null = newQrUrl
    if (qrOption === 'new' && newQrFile && !newQrUrl) {
      try {
        setNewQrUploading(true)
        const { uploadFilesWithPresignedPayload } = await import("../../../libs/presigned-upload")
        const results = await uploadFilesWithPresignedPayload([newQrFile], { folderName: "advance/qr-codes" })
        uploadedQrUrl = results[0]?.fileUrl || null
        setNewQrUrl(uploadedQrUrl)
      } catch {
        import("react-hot-toast").then(({ default: toast }) => toast.error("QR code upload failed"))
        setNewQrUploading(false)
        return
      } finally {
        setNewQrUploading(false)
      }
    }

    try {
      setRequestSubmitting(true)
      await axiosInstance.post("/claims/advance/request", {
        request_amount: amount,
        advance_reason: requestForm.advance_reason,
        request_remarks: requestForm.request_remarks.trim() || undefined,
        use_qr_code: qrOption !== 'none',
        use_existing_qr: qrOption === 'existing',
        payment_qr_code: qrOption === 'new' ? uploadedQrUrl : undefined,
      })
      setRequestForm({ request_amount: "", advance_reason: "site_work", request_remarks: "" })
      setQrOption('none')
      setNewQrFile(null)
      setNewQrUrl(null)
      setIsRequestModalOpen(false)
      onAdvanceCreated?.()
    } catch (error) {
      console.error("Error requesting advance:", error)
    } finally {
      setRequestSubmitting(false)
    }
  }

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => filters[key as keyof AdvanceFilters]).length
  }

  const formatCurrency = (amount?: number | null) => {
    if (!amount) return "₹0"
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        icon: FaClock,
        bgColor: "bg-gray-100",
        textColor: "text-gray-800",
        label: "Pending",
      },
      submitted: {
        icon: FaClock,
        bgColor: "bg-blue-100",
        textColor: "text-blue-800",
        label: "Submitted",
      },
      approved: {
        icon: FaCheckCircle,
        bgColor: "bg-green-100",
        textColor: "text-green-800",
        label: "Approved",
      },
      rejected: {
        icon: FaTimesCircle,
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        label: "Rejected",
      },
      transferred: {
        icon: FaCheckCircle,
        bgColor: "bg-purple-100",
        textColor: "text-purple-800",
        label: "Transferred",
      },
      completed: {
        icon: FaCheckCircle,
        bgColor: "bg-teal-100",
        textColor: "text-teal-800",
        label: "Completed",
      },
    }

    const config = statusConfig[status as keyof typeof statusConfig] || {
      icon: FaExclamationCircle,
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
      label: status,
    }

    const IconComponent = config.icon

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      >
        <IconComponent className="w-2 h-2" />
        {config.label}
      </span>
    )
  }

  const getAssociationBadge = (type: string) => {
    return type === "inhouse" ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
        Inhouse
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
        Vendor
      </span>
    )
  }

  const getAdvanceReason = (reason: string) => {
    const reasonMap: { [key: string]: string } = {
      site_work: "Site Work",
      travel: "Travel",
      material: "Material Purchase",
      other: "Other",
    }
    return reasonMap[reason] || reason
  }

  const getAppliedFilters = () => appliedFilters ?? {}

  const getFullName = (person?: { first_name?: string; last_name?: string } | null) =>
    `${person?.first_name || ""} ${person?.last_name || ""}`.trim() || "N/A"

  // Circle / reporting person / department group are stored on the advance
  // when it is requested, so later changes to the user don't rewrite old
  // claims. The live employee values are only a fallback for rows that were
  // never snapshotted.
  const getCircleName = (advance: Advance) =>
    advance.snapshot_taken_at
      ? advance.snapshot_circle_name || "N/A"
      : advance.employee?.state?.circle_name || "N/A"

  const getReportingPersonName = (advance: Advance) =>
    advance.snapshot_taken_at
      ? advance.snapshot_reporting_person_name || "N/A"
      : getFullName(advance.employee?.project_manager)

  const getDepartmentGroupName = (advance: Advance) => {
    if (advance.snapshot_taken_at) return advance.snapshot_department_group_name || "N/A"
    const departmentName = advance.employee?.department?.name?.trim()
    if (departmentName) return departmentName

    const groupNames = Array.from(new Set(
      (advance.employee?.user_projects || [])
        .map((item) => item.project?.client_project?.client_project_name?.trim())
        .filter((value): value is string => Boolean(value))
    ))

    return groupNames.length ? groupNames.join(", ") : "N/A"
  }

  const getApprovedAmount = (advance: Advance) =>
    ["approved", "transferred", "completed", "paid"].includes(advance.status) ? (advance.approved_amount || advance.request_amount) : null

  const getTransferredAmount = (advance: Advance) =>
    ["transferred", "completed", "paid"].includes(advance.status)
      ? (advance.transferred_amount ?? advance.approved_amount ?? advance.request_amount)
      : null

  const transformFiltersForApi = (filters: AdvanceFilters = {}) => {
    const transformed: Record<string, any> = { ...filters }
    if (transformed.created_from) {
      transformed.created_at_from = transformed.created_from
      delete transformed.created_from
    }
    if (transformed.created_to) {
      transformed.created_at_to = transformed.created_to
      delete transformed.created_to
    }
    return transformed
  }

  const openTransferDrawer = (advance: Advance) => {
    setTransferAdvance(advance)
    const approvedOrRequested = advance.approved_amount ?? advance.request_amount ?? 0
    setTransferData({
      transferred_amount: advance.transferred_amount ?? approvedOrRequested,
      transfer_mode: advance.transfer_mode || 'bank_transfer',
      transfer_reference: advance.transfer_reference || '',
      transfer_remarks: advance.transfer_remarks || '',
      transfer_date: advance.transferred_date ? advance.transferred_date.split('T')[0] : '',
      transfer_time: advance.transferred_time || '',
    })
    setTransferErrors({})
    setTransferTouched({})
  }

  const validateTransferForm = () => {
    const newErrors: Record<string, string> = {}
    if (!transferAdvance) return false
    const approvedOrRequested = transferAdvance.approved_amount ?? transferAdvance.request_amount ?? 0
    if (transferData.transferred_amount < 0) newErrors.transferred_amount = 'Amount cannot be negative'
    else if (transferData.transferred_amount > approvedOrRequested) newErrors.transferred_amount = 'Cannot exceed approved amount'
    if (!transferData.transfer_date) newErrors.transfer_date = 'Transfer date is required'
    setTransferErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleTransfer = async () => {
    if (!validateTransferForm() || !transferAdvance) return
    try {
      setTransferLoading(true)
      const payload: Record<string, unknown> = {
        id: Number(transferAdvance.id),
        transferred_amount: transferData.transferred_amount,
        transfer_mode: transferData.transfer_mode,
        transfer_reference: transferData.transfer_reference,
        transfer_remarks: transferData.transfer_remarks,
        transfer_date: transferData.transfer_date,
      }
      if (transferData.transfer_time) payload.transfer_time = transferData.transfer_time
      await axiosInstance.put('/claims/advance/transfer', payload)
      setTransferAdvance(null)
      onRefresh?.()
    } catch (error) {
      console.error('Error transferring advance:', error)
    } finally {
      setTransferLoading(false)
    }
  }

  // Complete stage: the reference number is optional at transfer and
  // mandatory here.
  const openCompleteModal = (advance: Advance) => {
    setCompleteAdvance(advance)
    setCompleteData({ transfer_reference: advance.transfer_reference || '', completion_remarks: '' })
    setCompleteError('')
  }

  const handleComplete = async () => {
    if (!completeAdvance) return
    if (!completeData.transfer_reference.trim()) {
      setCompleteError('Reference number is required')
      return
    }
    try {
      setCompleteLoading(true)
      setCompleteError('')
      await axiosInstance.put('/claims/advance/complete', {
        id: Number(completeAdvance.id),
        transfer_reference: completeData.transfer_reference.trim(),
        ...(completeData.completion_remarks.trim() ? { completion_remarks: completeData.completion_remarks.trim() } : {}),
      })
      setCompleteAdvance(null)
      onRefresh?.()
    } catch (error: any) {
      setCompleteError(error?.response?.data?.message || 'Failed to complete advance')
    } finally {
      setCompleteLoading(false)
    }
  }

  const getSearchParam = () => (searchValue ?? "").trim() || searchTerm.trim()

  const mapAdvanceForExport = (advance: Advance) => {
    const employeeName = `${advance.employee?.first_name || ""} ${advance.employee?.last_name || ""}`.trim()
    const approverName = advance.approver
      ? `${advance.approver.first_name} ${advance.approver.last_name}`.trim()
      : ""
    const transferrerName = advance.transferrer
      ? `${advance.transferrer.first_name} ${advance.transferrer.last_name}`.trim()
      : ""
    const rejectorName = advance.rejector
      ? `${advance.rejector.first_name || ""} ${advance.rejector.last_name || ""}`.trim()
      : ""

    return {
      "Employee Name": employeeName || "Unknown",
      "Employee Code": advance.employee?.employee_code || "",
      "Association Type": advance.employee?.association_type || "",
      "Accounting Name": advance.accounting_name || "",
      "Advance Reason": getAdvanceReason(advance.advance_reason),
      "Request Amount": advance.request_amount ?? 0,
      "Approved Amount": getApprovedAmount(advance) ?? "",
      "Transferred Amount": getTransferredAmount(advance) ?? "",
      "Transfer Mode": advance.transfer_mode || "",
      "Transfer Reference": advance.transfer_reference || "",
      "Transfer Remarks": advance.transfer_remarks || "",
      "Request Remarks": advance.request_remarks || "",
      Status: advance.status,
      "Circle Name": getCircleName(advance),
      "Reporting Person": getReportingPersonName(advance),
      "Department Group Name": getDepartmentGroupName(advance),
      "Approved By": approverName,
      "Approved Date": formatExcelDate(advance.approved_date),
      "Approval Remarks": advance.approval_remarks || "",
      "Rejection Reason": advance.rejection_reason || "",
      "Rejected By": rejectorName,
      "Rejected At": formatExcelDate(advance.rejected_at),
      "Transferred By": transferrerName,
      "Transferred Date": formatExcelDate(advance.transferred_date),
      "Transferred Time": advance.transferred_time || "",
      "Completed By": advance.completer ? `${advance.completer.first_name} ${advance.completer.last_name}`.trim() : "",
      "Completed At": formatExcelDate(advance.completed_at),
      "Completion Remarks": advance.completion_remarks || "",
      "Created At": formatExcelDate(advance.created_at),
      "Updated At": formatExcelDate(advance.updated_at),
    }
  }

  const fetchExportData = async () => {
    if (!canViewAllAdvances) return

    try {
      setExporting(true)
      const baseFilters = transformFiltersForApi(getAppliedFilters())
      const baseParams: Record<string, any> = { ...baseFilters }

      const searchParam = getSearchParam()
      if (searchParam) baseParams.search = searchParam
      if (statusFilter) baseParams.status = statusFilter

      if (sortConfig?.field) {
        baseParams.sort_by = sortConfig.field
        baseParams.sort_order = sortConfig.direction
      }

      Object.keys(baseParams).forEach((key) => {
        if (baseParams[key] === undefined || baseParams[key] === null || baseParams[key] === "") {
          delete baseParams[key]
        }
      })

      const allAdvances: Advance[] = []
      let currentPage = 1
      let totalPages = 1
      const pageSize = Number(pagination?.limit) || 10

      do {
        const params = {
          ...baseParams,
          page: currentPage,
          limit: pageSize,
        }

        const response = await axiosInstance.get("/claims/advance/list", { params })
        if (!response.data.success) {
          break
        }

        const responseAdvances: Advance[] = response.data.data.advances || []
        allAdvances.push(...responseAdvances)

        const paginationInfo = response.data.data.pagination || {}
        totalPages =
          paginationInfo.total_pages ??
          paginationInfo.totalPages ??
          paginationInfo.total_pages ??
          paginationInfo.total_pages ??
          currentPage

        currentPage += 1
      } while (currentPage <= totalPages)

      setExportData(allAdvances.map(mapAdvanceForExport))
      setShowExcelModal(true)
    } catch (error) {
      console.error("Error exporting advances:", error)
    } finally {
      setExporting(false)
    }
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction =
      sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.field !== field) {
      return <FaSort className="w-3 h-3 text-gray-400" />
    }
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-600" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-600" />
    )
  }

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Advance Requests</h2>
            <p className="text-sm text-gray-600 mt-1">Manage and review employee advance requests</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {canViewAllAdvances && (
              <>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, remarks..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-64"
                  />
                  <FaSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                </div>

                <button
                  onClick={() => setIsFilterModalOpen(true)}
                  className={`inline-flex items-center gap-2 px-4 py-2 border rounded transition-colors text-sm font-medium ${getActiveFiltersCount() > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <FiFilter className="w-4 h-4" />
                  Filters
                  {getActiveFiltersCount() > 0 && (
                    <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </button>
              </>
            )}

            {hasPermission('advance_claim', 'submit') && (
              isBlocked ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded text-sm font-medium cursor-not-allowed" title={blockReason || 'You are blocked from raising advance claims'}>
                  <FiLock className="w-4 h-4" />
                  Advance Blocked
                </div>
              ) : (
                <button
                  onClick={() => setIsRequestModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors text-sm font-medium"
                >
                  <FaIndianRupeeSign className="w-4 h-4" />
                  Request Advance
                  {userLimit && <span className="text-xs bg-blue-700 px-1.5 py-0.5 rounded">≤₹{userLimit.max_amount.toLocaleString()}</span>}
                </button>
              )
            )}

            {canViewAllAdvances && hasPermission('advance_claim', 'manage_restrictions') && (
              <Link
                to="/claims/advance/restrictions"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                <FiSettings className="w-4 h-4" />
                Restrictions
              </Link>
            )}

            {canViewAllAdvances && (
              <button
                onClick={onRefresh}
                disabled={loading}
                title="Refresh advances"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            )}

            {canViewAllAdvances && (
              <select
                value={statusFilter}
                onChange={handleFilterChange}
                className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
              >
                <option value="">All Status</option>
                <option value="requested">Requested</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="transferred">Transferred</option>
                <option value="completed">Completed</option>
              </select>
            )}

            {canViewAllAdvances && (
              <button
                onClick={fetchExportData}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                <FaFileExcel className="w-4 h-4" />
                {exporting ? "Preparing..." : "Export"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Blocked banner */}
      {isBlocked && hasPermission('advance_claim', 'submit') && (
        <div className="bg-red-50 border border-red-200 rounded p-4 flex items-start gap-3">
          <FiLock className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Your advance claim access is blocked</p>
            {blockReason && <p className="text-xs text-red-600 mt-0.5">Reason: {blockReason}</p>}
            <p className="text-xs text-red-500 mt-1">Contact your manager or HR to resolve this.</p>
          </div>
        </div>
      )}

      {/* Limit banner */}
      {!isBlocked && userLimit && hasPermission('advance_claim', 'submit') && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-center gap-3">
          <FiAlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Your advance request is limited to <span className="font-semibold">₹{userLimit.max_amount.toLocaleString()}</span> per claim.
            {userLimit.reason && <span className="text-amber-600"> ({userLimit.reason})</span>}
          </p>
        </div>
      )}

      {canViewAllAdvances && (
        <AdvanceFilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          filters={filters}
          onFiltersChange={setFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
      )}

      {isRequestModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <form onSubmit={handleRequestAdvance} className="bg-white rounded shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Request Advance</h2>
                <button type="button" onClick={() => setIsRequestModalOpen(false)} className="text-white hover:text-gray-200">
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount
                  {userLimit && <span className="ml-2 text-xs text-amber-600 font-normal">(max ₹{userLimit.max_amount.toLocaleString()})</span>}
                </label>
                <input
                  type="number"
                  min="1"
                  max={userLimit ? userLimit.max_amount : undefined}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={requestForm.request_amount}
                  onChange={(e) => setRequestForm((prev) => ({ ...prev, request_amount: e.target.value }))}
                />
                {userLimit && Number(requestForm.request_amount) > userLimit.max_amount && (
                  <p className="text-xs text-red-600 mt-1">Amount cannot exceed ₹{userLimit.max_amount.toLocaleString()}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={requestForm.advance_reason}
                  onChange={(e) => setRequestForm((prev) => ({ ...prev, advance_reason: e.target.value }))}
                >
                  <option value="site_work">Site Work</option>
                  <option value="purchase">Purchase</option>
                  <option value="other_work">Other Work</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                <textarea
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={requestForm.request_remarks}
                  onChange={(e) => setRequestForm((prev) => ({ ...prev, request_remarks: e.target.value }))}
                  placeholder="Optional, at least 10 characters if provided"
                />
              </div>
              {/* QR Code Option */}
              <div className="border border-gray-200 rounded p-3 space-y-2 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700">Payment QR Code</label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="qr_option" value="none" checked={qrOption === 'none'} onChange={() => { setQrOption('none'); setNewQrFile(null); setNewQrUrl(null); }} className="text-blue-800" />
                    No QR code
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${hasProfileQr === false ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'}`}>
                    <input type="radio" name="qr_option" value="existing" checked={qrOption === 'existing'} disabled={hasProfileQr === false} onChange={() => { setQrOption('existing'); setNewQrFile(null); setNewQrUrl(null); }} className="text-blue-800" />
                    Use existing QR code (from my profile)
                    {hasProfileQr === false && (
                      <span className="text-xs text-gray-400">— no QR in your profile</span>
                    )}
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="qr_option" value="new" checked={qrOption === 'new'} onChange={() => setQrOption('new')} className="text-blue-800" />
                    Upload new QR code
                  </label>
                </div>
                {qrOption === 'new' && (
                  <div className="mt-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => { setNewQrFile(e.target.files?.[0] || null); setNewQrUrl(null); }}
                      className="w-full text-sm text-gray-600 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {newQrFile && <p className="text-xs text-gray-500 mt-1">{newQrFile.name}</p>}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setIsRequestModalOpen(false); setQrOption('none'); setNewQrFile(null); setNewQrUrl(null); }} className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={requestSubmitting || newQrUploading || (!!userLimit && Number(requestForm.request_amount) > userLimit.max_amount) || (qrOption === 'new' && !newQrFile && !newQrUrl)}
                  className="px-4 py-2 bg-blue-800 text-white rounded text-sm hover:bg-blue-900 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {newQrUploading ? "Uploading QR..." : requestSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Advances</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{pagination.total_count}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
              <FaCalendarAlt className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Requested</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {advances.filter(a => a.status === 'requested').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
              <FaClock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {advances.filter(a => a.status === 'approved').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
              <FaCheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Transferred</p>
              <p className="text-2xl font-bold text-purple-900 mt-1">
                {advances.filter(a => a.status === 'transferred').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded flex items-center justify-center">
              <FaCheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div> */}

      {!canViewAllAdvances ? (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-8 text-center">
          <FaExclamationCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900">Advance list access unavailable</h3>
          <p className="text-sm text-gray-600 mt-1">
            You can request an advance, but viewing advance records requires additional permission.
          </p>
        </div>
      ) : (
        <>
          {/* Table Section */}
          <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>

                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Circle Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reporting Person
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department Group Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Designation
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Association
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("advance_reason")}
                  >
                    <span>Advance Reason</span>
                    {getSortIcon("advance_reason")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("request_amount")}
                  >
                    <span>Request Amount</span>
                    {getSortIcon("request_amount")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("status")}
                  >
                    <span>Status</span>
                    {getSortIcon("status")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approved Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transferred Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approver Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("approved_date")}
                  >
                    <span>Approved Date</span>
                    {getSortIcon("approved_date")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("transferred_date")}
                  >
                    <span>Transfer Date</span>
                    {getSortIcon("transferred_date")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("created_at")}
                  >
                    <span>Request Date</span>
                    {getSortIcon("created_at")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("request_remarks")}
                  >
                    <span>Remarks</span>
                    {getSortIcon("request_remarks")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={16} className="px-4 py-12">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-600 text-sm">Loading advances...</p>
                    </div>
                  </td>
                </tr>
              ) : advances.length === 0 ? (
                <tr>
                  <td colSpan={16} className="px-4 py-12">
                    <div className="flex flex-col items-center justify-center">
                      <FaExclamationCircle className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 text-sm">No advances found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                advances.map((advance) => (
                  <tr key={advance.id} className="hover:bg-gray-50 transition-colors">
                    {/* Actions */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {hasPermission('advance_claim', 'view') && (
                          <a
                            href={`/claims/advance/${advance.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                            title="View advance"
                          >
                            <LuEye className="w-4 h-4" />
                          </a>
                        )}
                        {hasPermission('advance_claim', 'view_payment_info') && (
                          <button
                            type="button"
                            onClick={() => setPaymentInfoAdvance(advance)}
                            className="text-indigo-600 hover:text-indigo-800 p-2 hover:bg-indigo-50 rounded transition-colors"
                            title="View Bank & Payment Info"
                          >
                            <FaUniversity className="w-4 h-4" />
                          </button>
                        )}
                        {hasPermission('advance_claim', 'approve') && advance.status === 'approved' && (
                          <button
                            onClick={() => openTransferDrawer(advance)}
                            className="text-purple-600 hover:text-purple-800 p-2 hover:bg-purple-50 rounded transition-colors"
                            title="Transfer amount"
                          >
                            <FaMoneyBillWave className="w-4 h-4" />
                          </button>
                        )}
                        {hasPermission('advance_claim', 'approve') && advance.status === 'transferred' && (
                          <button
                            onClick={() => openCompleteModal(advance)}
                            className="text-teal-600 hover:text-teal-800 p-2 hover:bg-teal-50 rounded transition-colors"
                            title="Add reference details & complete"
                          >
                            <FaClipboardCheck className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Employee */}
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {advance.employee?.first_name || ""} {advance.employee?.last_name || ""}
                        </span>
                      </div>
                    </td>

                    {/* Employee Code */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {advance.employee?.employee_code || "N/A"}
                      </span>
                    </td>

                    {/* Circle Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getCircleName(advance)}
                      </span>
                    </td>

                    {/* Project Manager */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getReportingPersonName(advance)}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getDepartmentGroupName(advance)}
                      </span>
                    </td>

                    {/* Designation */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {advance.employee?.designation?.name || "N/A"}
                      </span>
                    </td>

                    {/* Association */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getAssociationBadge(advance.employee.association_type)}
                    </td>

                    {/* Advance Reason */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getAdvanceReason(advance.advance_reason)}</span>
                    </td>

                    {/* Request Amount */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {/* <FaRupeeSign className="w-3 h-3 text-gray-400" /> */}
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(advance.request_amount)}
                        </span>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(advance.status)}</td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getApprovedAmount(advance) !== null ? formatCurrency(getApprovedAmount(advance)) : "N/A"}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getTransferredAmount(advance) !== null ? formatCurrency(getTransferredAmount(advance)) : "N/A"}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getFullName(advance.approver)}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{advance.approved_date ? formatDate(advance.approved_date) : "N/A"}</span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{advance.transferred_date ? formatDateOnly(advance.transferred_date) : "N/A"}</span>
                    </td>

                    {/* Request Date */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-900">{formatDate(advance.created_at)}</span>
                      </div>
                    </td>

                    {/* Remarks */}
                    <td className="px-4 py-4">
                      <span
                        className="text-sm text-gray-600 truncate max-w-[200px] block"
                        title={advance.request_remarks}
                      >
                        {advance.request_remarks || "-"}
                      </span>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <Pagination pagination={pagination as any} onPageChange={onPageChange} onLimitChange={onLimitChange} />

          {/* Excel Download Modal */}
          <ExcelDownload
            isOpen={showExcelModal}
            onClose={() => setShowExcelModal(false)}
            data={exportData}
            filename="advance_claims"
            title="Export Advances Data"
            description="Download detailed advance request records"
          />
        </>
      )}

      {/* Payment Info Modal */}
      {paymentInfoAdvance && hasPermission('advance_claim', 'view_payment_info') && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <FaUniversity className="w-5 h-5" />
                Payment Information
              </h2>
              <button type="button" onClick={() => setPaymentInfoAdvance(null)} className="text-white hover:text-gray-200">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Employee Info */}
              <div className="bg-gray-50 rounded p-3 space-y-1">
                <div>
                  <label className="text-xs font-medium text-gray-500">Employee Name</label>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">
                    {paymentInfoAdvance.employee?.first_name || ''} {paymentInfoAdvance.employee?.last_name || ''}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Department / Group</label>
                  <p className="text-sm text-gray-900 mt-0.5">{getDepartmentGroupName(paymentInfoAdvance)}</p>
                </div>
              </div>
              {/* Bank Details */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Bank Name</label>
                  <p className="text-sm text-gray-900 mt-0.5">{paymentInfoAdvance.bank_name || paymentInfoAdvance.employee.user_details?.bank_name || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Account Number</label>
                  <p className="text-base text-gray-900 mt-0.5 font-mono tracking-wide">{paymentInfoAdvance.bank_account_number || paymentInfoAdvance.employee.user_details?.bank_account_number || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">IFSC Code</label>
                  <p className="text-base text-gray-900 mt-0.5 font-mono tracking-wide">{paymentInfoAdvance.ifsc_code || paymentInfoAdvance.employee.user_details?.ifsc_code || '-'}</p>
                </div>
              </div>
              {/* QR Code */}
              {(paymentInfoAdvance.payment_qr_code || paymentInfoAdvance.employee.user_details?.payment_qr_code) && (
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-2">
                    Payment QR Code
                    {paymentInfoAdvance.payment_qr_code && (
                      <span className="ml-2 text-xs text-blue-600">
                        ({paymentInfoAdvance.use_existing_qr ? 'From profile' : 'Uploaded with request'})
                      </span>
                    )}
                  </label>
                  <img
                    src={paymentInfoAdvance.payment_qr_code || paymentInfoAdvance.employee.user_details?.payment_qr_code || ''}
                    alt="Payment QR"
                    className="w-64 h-64 object-contain border border-gray-200 rounded mx-auto"
                  />
                </div>
              )}
              {!paymentInfoAdvance.payment_qr_code && !paymentInfoAdvance.employee.user_details?.payment_qr_code && (
                <p className="text-xs text-gray-400 text-center">No QR code attached</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transfer Amount Drawer */}
      {transferAdvance && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
            onClick={!transferLoading ? () => setTransferAdvance(null) : undefined}
          />
          <div className="overflow-y-auto fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Transfer Amount</h3>
                <p className="text-sm text-gray-600 mt-1">Confirm transfer details</p>
              </div>
              <button
                onClick={() => setTransferAdvance(null)}
                className="p-2 hover:bg-gray-200 rounded transition-colors"
                disabled={transferLoading}
              >
                <FiX size={20} className="text-gray-600" />
              </button>
            </div>

            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Amount *</label>
                  <input
                    type="number"
                    value={transferData.transferred_amount}
                    onChange={(e) => {
                      setTransferData({ ...transferData, transferred_amount: Number(e.target.value) })
                      setTransferTouched({ ...transferTouched, transferred_amount: true })
                      if (transferErrors.transferred_amount) setTransferErrors({ ...transferErrors, transferred_amount: '' })
                    }}
                    max={transferAdvance.approved_amount ?? transferAdvance.request_amount ?? 0}
                    disabled={transferLoading}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${transferErrors.transferred_amount && transferTouched.transferred_amount ? 'border-red-300 bg-red-50' : 'border-gray-300'} disabled:bg-gray-50`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Approved: ₹{(transferAdvance.approved_amount ?? transferAdvance.request_amount ?? 0).toLocaleString('en-IN')}
                  </p>
                  {transferErrors.transferred_amount && transferTouched.transferred_amount && (
                    <p className="mt-1 text-sm text-red-600">{transferErrors.transferred_amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Mode</label>
                  <select
                    value={transferData.transfer_mode}
                    onChange={(e) => setTransferData({ ...transferData, transfer_mode: e.target.value })}
                    disabled={transferLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Date *</label>
                  <input
                    type="date"
                    value={transferData.transfer_date}
                    onChange={(e) => {
                      setTransferData({ ...transferData, transfer_date: e.target.value })
                      setTransferTouched({ ...transferTouched, transfer_date: true })
                      if (transferErrors.transfer_date) setTransferErrors({ ...transferErrors, transfer_date: '' })
                    }}
                    disabled={transferLoading}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${transferErrors.transfer_date && transferTouched.transfer_date ? 'border-red-300 bg-red-50' : 'border-gray-300'} disabled:bg-gray-50`}
                  />
                  {transferErrors.transfer_date && transferTouched.transfer_date && (
                    <p className="mt-1 text-sm text-red-600">{transferErrors.transfer_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Time (24H Format)</label>
                  <TimeInput24h
                    value={transferData.transfer_time}
                    onChange={(time: string) => setTransferData({ ...transferData, transfer_time: time })}
                    disabled={transferLoading}
                    hasError={false}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number (Optional)</label>
                  <input
                    type="text"
                    value={transferData.transfer_reference}
                    onChange={(e) => {
                      setTransferData({ ...transferData, transfer_reference: e.target.value })
                      setTransferTouched({ ...transferTouched, transfer_reference: true })
                      if (transferErrors.transfer_reference) setTransferErrors({ ...transferErrors, transfer_reference: '' })
                    }}
                    disabled={transferLoading}
                    placeholder="Transaction ID / Reference"
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${transferErrors.transfer_reference && transferTouched.transfer_reference ? 'border-red-300 bg-red-50' : 'border-gray-300'} disabled:bg-gray-50`}
                  />
                  {transferErrors.transfer_reference && transferTouched.transfer_reference && (
                    <p className="mt-1 text-sm text-red-600">{transferErrors.transfer_reference}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Remarks (Optional)</label>
                  <textarea
                    value={transferData.transfer_remarks}
                    onChange={(e) => setTransferData({ ...transferData, transfer_remarks: e.target.value })}
                    rows={2}
                    disabled={transferLoading}
                    placeholder="Add any remarks..."
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded p-4">
                  <div className="flex items-start gap-3">
                    <FaMoneyBillWave className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Transfer Guidelines</p>
                      <ul className="text-xs space-y-1 list-disc list-inside">
                        <li>Amount can only be equal to or less than approved</li>
                        <li>Verify recipient details before transfer</li>
                        <li>Keep the reference number for records</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t bg-gray-50 px-6 py-4">
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button
                    onClick={() => setTransferAdvance(null)}
                    disabled={transferLoading}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTransfer}
                    disabled={transferLoading || transferData.transferred_amount < 0 || !transferData.transfer_date}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {transferLoading ? 'Transferring...' : 'Complete Transfer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Complete Advance Modal */}
      {completeAdvance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-white rounded shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Complete Advance</h3>
                <p className="text-sm text-gray-600 mt-1">Add the reference details of the transfer</p>
              </div>
              <button
                onClick={() => setCompleteAdvance(null)}
                className="p-2 hover:bg-gray-200 rounded transition-colors"
                disabled={completeLoading}
              >
                <FiX size={20} className="text-gray-600" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Employee</span>
                  <span className="font-medium text-gray-900">
                    {`${completeAdvance.employee?.first_name ?? ''} ${completeAdvance.employee?.last_name ?? ''}`.trim() || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Transferred Amount</span>
                  <span className="font-medium text-gray-900">₹{getTransferredAmount(completeAdvance) ?? 0}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number *</label>
                <input
                  type="text"
                  value={completeData.transfer_reference}
                  maxLength={255}
                  onChange={(e) => setCompleteData({ ...completeData, transfer_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter reference number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (Optional)</label>
                <textarea
                  value={completeData.completion_remarks}
                  rows={3}
                  onChange={(e) => setCompleteData({ ...completeData, completion_remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Add remarks..."
                />
              </div>
              {completeError && (
                <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{completeError}</div>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setCompleteAdvance(null)}
                  disabled={completeLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleComplete}
                  disabled={completeLoading || !completeData.transfer_reference.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  {completeLoading ? 'Completing...' : 'Mark Complete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
